// Data shaping for the per-strategy journey cards on the Cost Benefit view.
// Pure functions over engine output — every dollar here comes from the scenario's
// server-computed results (hpeCashFlows, estimatedFutureTaxAvoided, totalEconomicCost);
// nothing is interpolated or smoothed except the breakeven crossing point and the
// Roth spread below.

import type { TaxScenario } from './types';
import { fiaTermYears } from './rothGrowth';

/** Every Cost Benefit horizon runs at least this far — "8 years across the board" (Andrew,
 *  2026-09-15). Before this floor a Roth-only strategy, or the Conservative toggle's
 *  7-entry return-of-capital schedule, silently pulled the horizon back to 6. */
export const PAYOFF_HORIZON_FLOOR_YEARS = 8;

/** The Roth's estimated future taxes avoided are credited in equal slices across the payoff
 *  horizon (static-growth assumption — Drew's call, 2026-08-25), then hold flat.
 *
 *  MUST equal PAYOFF_HORIZON_FLOOR_YEARS. This was 6 while the horizon moved to 8, so the
 *  Roth line reached its full value at Year 6 and sat flat through 7 and 8. On screen that
 *  reads as "the calculator stops at year 6" — Dylan and Drew both hit it on the 2026-09-22
 *  user call, and recalculating never helped because the number is a client constant, not
 *  stored data. Nothing pinned the two together, so they drifted silently while the existing
 *  floor guard (PAYOFF vs the engine's ROTH_PROJECTION_FLOOR_YEARS) stayed green.
 *  A test now asserts this equality.
 *
 *  Alt cash follows the engine's hpeCashFlows, which is the fund exhibit's schedule. */
export const JOURNEY_ROTH_SPREAD_YEARS = PAYOFF_HORIZON_FLOOR_YEARS;

export interface JourneyPoint {
  year: number;
  /** Cumulative position vs baseline: money back to date minus the Year-1 cost over baseline. */
  position: number;
  /** Cumulative alt cash received through this year: the fund exhibit's distributions, scaled. */
  altCashBack: number;
  /** Taxes avoided on the Roth through this year: estimatedFutureTaxAvoided credited in
   *  JOURNEY_ROTH_SPREAD_YEARS equal yearly slices, reaching the engine's full number at
   *  the payoff horizon floor exactly. */
  rothTaxAvoided: number;
}

export interface StrategyJourney {
  scenarioId: string;
  points: JourneyPoint[];
  /** Δ totalEconomicCost vs baseline — the same "Year 1 Cost Over Baseline" the old bars showed. */
  costOverBaseline: number;
  /** hpeFutureValue + estimatedFutureTaxAvoided — the same gross-return total the old bars showed. */
  grossReturn: number;
  /** grossReturn − costOverBaseline (identical to the old chart's +$ annotation). */
  netBenefit: number;
  /** Year the drawn position curve (recharts type="monotone") crosses zero, so the breakeven
   *  dot sits exactly on the rendered line. 0 = ahead from day one; null = never surfaces
   *  within the horizon. */
  breakevenYear: number | null;
  horizonYears: number;
}

/**
 * The one horizon every payoff label uses, journey cards or legacy bars, screen or export:
 * the 8-year floor, or further if any strategy's payback runs past it — the alt schedule's
 * last year or the Roth's growth years, whichever is furthest out. Never depends on a flag.
 */
/** How far a scenario's Roth projection actually reaches. An FIA model walks its own
 *  fixed sequence and ignores the stored growth-years, so the label follows the term. */
function rothReachYears(s: TaxScenario): number {
  const model = s.input.rothGrowthModel;
  if (model && model !== 'straight') return fiaTermYears(model);
  return Math.max(PAYOFF_HORIZON_FLOOR_YEARS, s.input.rothGrowthYears || 0);
}

export function payoffHorizonYears(scenarios: TaxScenario[]): number {
  const rothYears = Math.max(0, ...scenarios.map(rothReachYears));
  const altYears = Math.max(
    0,
    ...scenarios.map((s) => (s.input.alternativeInvestmentAmount > 0 ? (s.results.hpeCashFlows?.length ?? 1) - 1 : 0)),
  );
  return Math.max(PAYOFF_HORIZON_FLOOR_YEARS, rothYears, altYears);
}

/** A single strategy's horizon: the 8-year floor, or the fund schedule's last year if longer. */
export function journeyHorizon(scenario: TaxScenario): number {
  const hasAlt = scenario.input.alternativeInvestmentAmount > 0;
  const altYears = hasAlt ? (scenario.results.hpeCashFlows?.length ?? 1) - 1 : 0;
  return Math.max(PAYOFF_HORIZON_FLOOR_YEARS, altYears);
}

// ── Alt Key Takeaways: the two period-return figures (Andrew, 2026-09-15) ────
// "Annualized return and gross return for the 8-year period." Both come straight
// from the exhibit's own MOIC and the shared horizon — nothing new is modeled.
// Andrew's "31%" example was this same annualized math on the 6-year horizon the
// app showed before the floor fix; on 8 years the exhibit reads 22.8%.

/** Gross return over the horizon as a multiple of the investment: the exhibit's
 *  pre-tax MOIC (total cash back ÷ invested). 0 when the scenario has no alt. */
export function altGrossReturnMultiple(scenario: TaxScenario): number {
  return scenario.input.alternativeInvestmentAmount > 0 ? scenario.results.hpeMoic || 0 : 0;
}

/** That multiple spread evenly over the strategy's horizon: MOIC^(1/years) − 1.
 *  Conservative mode (1.00x) lands on exactly 0. 0 when the scenario has no alt. */
export function altAnnualizedReturn(scenario: TaxScenario): number {
  const moic = altGrossReturnMultiple(scenario);
  if (moic <= 0) return 0;
  return Math.pow(moic, 1 / journeyHorizon(scenario)) - 1;
}

// ── Monotone-curve math ──────────────────────────────────────────────────────
// The journey cards draw the position line with recharts type="monotone", which is
// d3-shape's curveMonotoneX (Fritsch–Carlson monotone cubic Hermite). The breakeven
// dot must sit exactly where that DRAWN curve crosses zero — not where straight
// segments between the yearly points would cross — so the same curve is evaluated
// here. Tangent formulas mirror d3-shape's slope2/slope3 with unit x-spacing.

function monotoneTangents(ys: number[]): number[] {
  const n = ys.length;
  if (n === 1) return [0];
  const s: number[] = [];
  for (let i = 0; i < n - 1; i++) s.push(ys[i + 1] - ys[i]);
  const m: number[] = new Array(n);
  for (let i = 1; i < n - 1; i++) {
    const s0 = s[i - 1];
    const s1 = s[i];
    const p = (s0 + s1) / 2;
    m[i] = (Math.sign(s0) + Math.sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0;
  }
  m[0] = (3 * s[0] - m[1]) / 2;
  m[n - 1] = (3 * s[n - 2] - m[n - 2]) / 2;
  return m;
}

/**
 * Value of the drawn monotone curve at fractional year x (x in [0, points-1]).
 * Passes through every yearly point exactly; between points it follows the same
 * cubic the chart renders.
 */
export function monotonePositionAt(positions: number[], x: number): number {
  const n = positions.length;
  if (n === 0) return 0;
  if (x <= 0) return positions[0];
  if (x >= n - 1) return positions[n - 1];
  const i = Math.floor(x);
  const u = x - i;
  const m = monotoneTangents(positions);
  const y0 = positions[i];
  const y1 = positions[i + 1];
  const h00 = 2 * u ** 3 - 3 * u ** 2 + 1;
  const h10 = u ** 3 - 2 * u ** 2 + u;
  const h01 = -2 * u ** 3 + 3 * u ** 2;
  const h11 = u ** 3 - u ** 2;
  return h00 * y0 + h10 * m[i] + h01 * y1 + h11 * m[i + 1];
}

/** First zero crossing of the drawn curve: 0 = ahead from day one, null = never surfaces. */
function findBreakevenOnCurve(positions: number[]): number | null {
  if (positions[0] >= 0) return 0;
  for (let t = 1; t < positions.length; t++) {
    if (positions[t - 1] < 0 && positions[t] >= 0) {
      if (positions[t] === 0) return t;
      // Bisect within the segment — the monotone cubic has a single crossing here.
      let lo = t - 1;
      let hi = t;
      for (let iter = 0; iter < 60; iter++) {
        const mid = (lo + hi) / 2;
        if (monotonePositionAt(positions, mid) < 0) lo = mid;
        else hi = mid;
      }
      return (lo + hi) / 2;
    }
  }
  return null;
}

export function buildStrategyJourney(
  strategy: TaxScenario,
  baseline: TaxScenario,
  horizonYears?: number,
): StrategyJourney {
  const horizon = Math.max(horizonYears ?? 0, journeyHorizon(strategy));
  const costOverBaseline = strategy.results.totalEconomicCost - baseline.results.totalEconomicCost;
  const futureTaxAvoided = strategy.results.estimatedFutureTaxAvoided || 0;
  const grossReturn = (strategy.results.hpeFutureValue || 0) + futureTaxAvoided;
  const flows = strategy.results.hpeCashFlows ?? [];

  const points: JourneyPoint[] = [];
  let altCashBack = 0;
  for (let t = 0; t <= horizon; t++) {
    // flows[0] is the Year-0 outflow. It is already inside costOverBaseline
    // (totalEconomicCost includes alternativeInvestmentAmount), so counting it
    // here would double-charge the client — only inflows accumulate.
    if (t >= 1 && t < flows.length) altCashBack += flows[t];
    const rothTaxAvoided =
      futureTaxAvoided * (Math.min(t, JOURNEY_ROTH_SPREAD_YEARS) / JOURNEY_ROTH_SPREAD_YEARS);
    points.push({
      year: t,
      position: altCashBack + rothTaxAvoided - costOverBaseline,
      altCashBack,
      rothTaxAvoided,
    });
  }

  const breakevenYear = findBreakevenOnCurve(points.map((p) => p.position));

  return {
    scenarioId: strategy.id,
    points,
    costOverBaseline,
    grossReturn,
    netBenefit: grossReturn - costOverBaseline,
    breakevenYear,
    horizonYears: horizon,
  };
}

// ── Repeat ladder: convert once, then the alt every year ─────────────────────
// Steve's ask (2026-09-04): show what doing this five consecutive years does, letting
// every cohort complete its full trajectory. Year one is the whole strategy exactly as
// the journey card scores it — cost over baseline, the alt's hpeCashFlows, the Roth's
// taxes avoided in six equal slices. Years two onward repeat only the alt, on the
// client's regular income: their cost is `repeatCostOverBaseline`, the engine-computed
// difference between the strategy and the same strategy with the alt set to zero
// (for an alt-only strategy that is the Year-1 cost itself). A Roth conversion and a
// stock sale happen once; they are never repeated. Assumes the same income and
// deduction room every year — a disclosed illustration assumption, not a projection.

export const LADDER_COHORTS = 5;

export interface LadderPoint {
  year: number;
  /** Capital put into the alt through this year (cohorts started × investment). */
  invested: number;
  /** Alt cash received across every cohort through this year. */
  altCashBack: number;
  /** Roth taxes avoided credited through this year (Year-1 conversion only, six equal slices). */
  rothTaxAvoided: number;
  /** Cumulative position vs doing nothing. */
  position: number;
}

export interface RepeatLadder {
  scenarioId: string;
  cohorts: number;
  points: LadderPoint[];
  totalInvested: number;
  /** Year-1 strategy cost plus (cohorts − 1) × the alt's own repeat cost. */
  totalCostOverBaseline: number;
  /** The alt's own cost over baseline in a repeat year (investment less the tax it saves on its own). */
  repeatCostOverBaseline: number;
  totalCashBack: number;
  totalRothTaxAvoided: number;
  netBenefit: number;
  breakevenYear: number | null;
  horizonYears: number;
}

export function buildRepeatLadder(
  strategy: TaxScenario,
  baseline: TaxScenario,
  opts: { cohorts?: number; repeatCostOverBaseline?: number } = {},
): RepeatLadder {
  const cohorts = opts.cohorts ?? LADDER_COHORTS;
  const flows = strategy.results.hpeCashFlows ?? [];
  const altYears = Math.max(0, flows.length - 1);
  const cost = strategy.results.totalEconomicCost - baseline.results.totalEconomicCost;
  const repeatCost = opts.repeatCostOverBaseline ?? cost;
  const amt = strategy.input.alternativeInvestmentAmount;
  const cashPerCohort = flows.slice(1).reduce((a, b) => a + b, 0);
  const fta = strategy.results.estimatedFutureTaxAvoided || 0;
  const horizon = Math.max(cohorts - 1 + altYears, fta > 0 ? JOURNEY_ROTH_SPREAD_YEARS : 0);

  const points: LadderPoint[] = [];
  for (let t = 0; t <= horizon; t++) {
    const started = Math.min(t + 1, cohorts);
    let altCashBack = 0;
    for (let c = 0; c < started; c++) {
      for (let k = 1; k <= t - c && k < flows.length; k++) altCashBack += flows[k];
    }
    const rothTaxAvoided = fta * (Math.min(t, JOURNEY_ROTH_SPREAD_YEARS) / JOURNEY_ROTH_SPREAD_YEARS);
    const costSoFar = cost + repeatCost * (started - 1);
    points.push({
      year: t,
      invested: amt * started,
      altCashBack,
      rothTaxAvoided,
      position: altCashBack + rothTaxAvoided - costSoFar,
    });
  }

  const totalCost = cost + repeatCost * (cohorts - 1);
  return {
    scenarioId: strategy.id,
    cohorts,
    points,
    totalInvested: amt * cohorts,
    totalCostOverBaseline: totalCost,
    repeatCostOverBaseline: repeatCost,
    totalCashBack: cashPerCohort * cohorts,
    totalRothTaxAvoided: fta,
    netBenefit: cashPerCohort * cohorts + fta - totalCost,
    breakevenYear: findBreakevenOnCurve(points.map((p) => p.position)),
    horizonYears: horizon,
  };
}
